import { SettingOutlined, UserOutlined } from '@ant-design/icons';
import { ClassKey } from "@const/class.constants";
import { HwTypeKey } from '@const/hwTypeKey.constants';
import * as _ from 'lodash-es';
import { FC, Suspense, lazy } from 'react';
import { BsBookmarkStar } from 'react-icons/bs';
import { XmSpinner } from 'src/component/spinner/XmSpinner';
import { XmWarningBadge } from 'src/component/warningBadge/XmWarningBadge';
import { BasePage } from 'src/content/basePage/BasePage';
import Skytap from 'src/content/configuration/device/lpar/cloud/Skytap';
import BellIcon from 'src/content/globalHealthStatus/BellIcon';
import Search from 'src/content/search/components/SearchResultsPage';
import cfgTracker, { CFGTrackerWithTabs } from 'src/content/tools/confChanges/CFGTracker';
import { GLOB } from "src/util/Glob";
import { useAppGlobStore } from "../appGlobalStore";
import { cmdbChildren, topMenuToMenuItemsRight, topologyChildren, transformTopologyMenu } from "../content/tools/cmdb/CMDB";
import { MenuNode } from '../model/MenuNode';
import { API_URL } from './Api';
import { RoutePath } from './Routes';
import CustomLinks from "../content/tools/cmdb/customLinks/CustomLinks";

const CFGTracker = cfgTracker(CFGTrackerWithTabs);

const About = lazy(() => import('src/content/help/about/About'));
const Alerting = lazy(() => import('../content/tools/alerting/Alerting'));
const Aliases = lazy(() => import('src/content/configuration/alias/Aliases'));
const ApplicationCfg = lazy(() => import('../content/configuration/application/ApplicationCfg'));
const CustomGroups = lazy(() => import('../content/configuration/cg/CustomGroups'));
const Dashboard = lazy(() => import('src/content/configuration/dashboard/Dashboard'));
const ExpImpCfg = lazy(() => import('src/content/configuration/exportImport/ExpImpCfg'));
const Exporter = lazy(() => import('../content/tools/exporter/Exporter'));
const HeatmapTierHome = lazy(() => import('../content/configuration/heatmapTiers/HeatmapTierHome'));
const License = lazy(() => import('src/content/help/license/License'));
const Management = lazy(() => import('../content/configuration/userManagement/management'));
const MissingTech = lazy(() => import('src/content/configuration/device/MissingTech'));
const ProfileTabs = lazy(() => import('src/content/userProfile/ProfileTabs'));
const ProxyAgent = lazy(() => import('src/content/configuration/proxy/ProxyAgent'));
const Reporter = lazy(() => import('../content/tools/reporter/components/Reporter'));
const Grouping = lazy(() => import('../content/configuration/grouping/Grouping'));
const Support = lazy(() => import('src/content/help/support/Support'));
const Tag = lazy(() => import('src/content/tools/tag/Tag'));
const UpgradeApp = lazy(() => import('src/content/help/upgrade/UpgradeApp'));

const BackupHostCfg = lazy(() => import('src/content/configuration/device/stor/BackupHostCfg'));
const LanHostCfg = lazy(() => import('src/content/configuration/device/stor/LanHostCfg'));
const SanHostCfg = lazy(() => import('src/content/configuration/device/stor/SanHostCfg'));
const StorageHostCfg = lazy(() => import('src/content/configuration/device/stor/StorageHostCfg'));
const TapeHostCfg = lazy(() => import('src/content/configuration/device/stor/TapeHostCfg'));
const ServerSPHostCfg = lazy(() => import('src/content/configuration/device/stor/ServerSPHostCfg'));

const Amazon = lazy(() => import('src/content/configuration/device/lpar/cloud/Amazon'));
const Azure = lazy(() => import('../content/configuration/device/lpar/cloud/Azure'));
const Db2 = lazy(() => import('src/content/configuration/device/lpar/db/Db2'));
const Docker = lazy(() => import('../content/configuration/device/lpar/container/Docker'));
const Gcloud = lazy(() => import('src/content/configuration/device/lpar/cloud/Gcloud'));
const IBMCloud = lazy(() => import('src/content/configuration/device/lpar/cloud/IBMCloud'));
const Kubernetes = lazy(() => import('../content/configuration/device/lpar/container/Kubernetes'));
const Mainframe = lazy(() => import('src/content/configuration/device/lpar/server/Mainframe'));
const MongoDB = lazy(() => import("../content/configuration/device/lpar/db/MongoDB"));
const Nutanix = lazy(() => import('src/content/configuration/device/lpar/server/Nutanix'));
const Morpheus = lazy(() => import('src/content/configuration/device/lpar/server/Morpheus'));
const OpenShift = lazy(() => import('src/content/configuration/device/lpar/container/OpenShift'));
const Oracle = lazy(() => import('src/content/configuration/device/lpar/db/Oracle'));
const OracleVM = lazy(() => import('src/content/configuration/device/lpar/server/OracleVM'));
const OSagent = lazy(() => import('src/content/configuration/device/lpar/server/OSagent'));
const Postgres = lazy(() => import('../content/configuration/device/lpar/db/Postgres'));
const Power = lazy(() => import('../content/configuration/device/lpar/server/power/Power'));
const Proxmox = lazy(() => import('../content/configuration/device/lpar/server/Proxmox'));
const RHVoVirt = lazy(() => import('src/content/configuration/device/lpar/server/RHVoVirt'));
const SqlServer = lazy(() => import('src/content/configuration/device/lpar/db/SqlServer'));
const VMware = lazy(() => import('../content/configuration/device/lpar/server/VMware'));
const Xenserver = lazy(() => import('../content/configuration/device/lpar/server/Xenserver'));
const Tanzu = lazy(() => import('../content/configuration/device/lpar/container/Tanzu'));

const Pie = lazy(() => import.meta.env.VITE_LEGACY === 'true' ? Promise.resolve({ default: (() => null) as FC }) : import('../content/playground/pie'));
const Sunburst = lazy(() => import.meta.env.VITE_LEGACY === 'true' ? Promise.resolve({ default: (() => null) as FC }) : import('../content/playground/sunburst'));
const Plot = lazy(() => import.meta.env.VITE_LEGACY === 'true' ? Promise.resolve({ default: (() => null) as FC }) : import('src/content/playground/plot'));
export function resetMenuItemsRight() {
  const cmdbChildren = topMenuToMenuItemsRight(useAppGlobStore.getState().topMenu.filter(item => item.key !== '/dashboard' && item.key !== '/custom'));
  menuItemsRight = [..._.cloneDeep(menuItemsRightTemplate)];
  const settingsIdx = menuItemsRight.findIndex(item => item.key === RoutePath.SETTINGS);
  const toolsIdx = menuItemsRight[settingsIdx].children.findIndex(item => item.key === RoutePath.TOOLS);
  const cmdbIndex = menuItemsRight[settingsIdx].children[toolsIdx].children.findIndex(item => item.key === RoutePath.CMDB);
  menuItemsRight[settingsIdx].children[toolsIdx].children[cmdbIndex].children.push(...cmdbChildren);
  const topologyChildren = transformTopologyMenu(useAppGlobStore.getState().topologyMenu);
  const topologyIndex = menuItemsRight[settingsIdx].children[toolsIdx].children.findIndex(item => item.key === RoutePath.TOPOLOGY);
  menuItemsRight[settingsIdx].children[toolsIdx].children[topologyIndex].children = topologyChildren;
}
export let menuItemsRight: MenuNode[];
const menuItemsRightTemplate: readonly Readonly<MenuNode>[] = [
  {
    key: RoutePath.SEARCH,
    title: 'Search',
    path: RoutePath.SEARCH,
    component: <Search />,
    isLeaf: true,
    // icon is defined in TopNavBar comp
  },
  {
    key: RoutePath.GLOB_HS,
    icon: <BellIcon />,
    path: RoutePath.GLOB_HS,
    isLeaf: true
  },
  {
    key: RoutePath.FAVORITES,
    icon: <div className='xm-icon-wrapper' data-testid='top-menu-favorites'><BsBookmarkStar style={{ strokeWidth: .3, paddingBottom: 2 }} /></div>,
    path: RoutePath.FAVORITES,
    isLeaf: true
  },
  {
    key: RoutePath.SETTINGS,
    title: 'Settings',
    icon: <XmWarningBadge hostcfg={true} license={true} certificate={true}><SettingOutlined /></XmWarningBadge>,
    path: RoutePath.SETTINGS,
    children: [
      {
        key: RoutePath.CONFIGURATION,
        title: 'Configuration',
        path: RoutePath.CONFIGURATION,
        children: [
          {
            key: RoutePath.SET_DEVICE,
            title: <XmWarningBadge offset={[3, 0]} hostcfg={true} license={false} certificate={false}>Device</XmWarningBadge>,
            titleText: 'Device',
            path: RoutePath.SET_DEVICE,
            isLeaf: false,
            adminFeature: 'deviceConfig',
            children: [
              {
                key: RoutePath.STORAGE,
                title: 'Storage',
                path: RoutePath.STORAGE,
                isLeaf: true,
                component: (
                  <Suspense fallback={<XmSpinner />}>
                    <StorageHostCfg />
                  </Suspense>
                ),
                classType: ClassKey.STORAGE
              },
              {
                key: RoutePath.SERVER,
                title: 'Server',
                path: RoutePath.SERVER,
                isLeaf: false,
                children: [
                  {
                    key: RoutePath.MORPHEUS_CFG,
                    title: 'HPE Morpheus VME',
                    path: RoutePath.MORPHEUS_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Morpheus />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.MORPHEUS
                  },
                  // {
                  //   key: RoutePath.HUAWEI,
                  //   title: 'Huawei FusionCompute',
                  //   path: RoutePath.HUAWEI,
                  //   component: (
                  //     <Suspense fallback={<XmSpinner />}>
                  //       <MissingTech technoName="Huawei FusionCompute" />
                  //     </Suspense>
                  //   ),
                  //   isLeaf: true,
                  //   hwType: HwTypeKey.HUAWEI,
                  // },
                  {
                    key: RoutePath.POWER_CFG,
                    title: 'IBM Power Systems',
                    path: RoutePath.POWER_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Power />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.POWER
                  },
                  {
                    key: RoutePath.MAINFRAME_CFG,
                    title: 'IBM Z Mainframe',
                    path: RoutePath.MAINFRAME_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Mainframe />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.MAINFRAME
                  },
                  {
                    key: RoutePath.NUTANIX_CFG,
                    title: 'Nutanix',
                    path: RoutePath.NUTANIX_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Nutanix />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.NUTANIX
                  },
                  {
                    key: RoutePath.ORACLEVM,
                    title: 'Oracle VM',
                    path: RoutePath.ORACLEVM,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <OracleVM />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.ORACLEVM,
                  },
                  {
                    key: RoutePath.PROXMOX_CFG,
                    title: 'Proxmox',
                    path: RoutePath.PROXMOX_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Proxmox />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.PROXMOX
                  },
                  {
                    key: RoutePath.OVIRT,
                    title: 'RHV/oVirt',
                    path: RoutePath.OVIRT,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <RHVoVirt />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.OVIRT,
                  },
                  {
                    key: RoutePath.VMWARE_CFG,
                    title: 'VMware',
                    path: RoutePath.VMWARE_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <VMware />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.VMWARE
                  },
                  {
                    key: RoutePath.XEN,
                    title: 'XenServer',
                    path: RoutePath.XEN,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Xenserver />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.XEN,
                  },
                ],
              },
              {
                key: RoutePath.SET_OS_AGENT,
                title: 'OS Agent',
                path: RoutePath.SET_OS_AGENT,
                component: <Suspense fallback={<XmSpinner />}>
                  <OSagent />
                </Suspense>,
                isLeaf: true,
                hwType: [
                  HwTypeKey.LINUX,
                  HwTypeKey.WINDOWS,
                  HwTypeKey.SOLARIS,
                  HwTypeKey.AS400,
                ],
                classType: ClassKey.VIRTUALIZATION
              },
              {
                key: RoutePath.SERVER_SP,
                title: 'Server SP',
                path: RoutePath.SERVER_SP,
                isLeaf: true,
                component: (
                  <Suspense fallback={<XmSpinner />}>
                    <ServerSPHostCfg />
                  </Suspense>
                ),
                classType: ClassKey.SERVERSP
              },
              {
                key: RoutePath.DATABASE,
                title: 'Database',
                path: RoutePath.DATABASE,
                isLeaf: false,
                children: [
                  {
                    key: RoutePath.DB2,
                    title: 'IBM Db2',
                    path: RoutePath.DB2,
                    component: <Suspense fallback={<XmSpinner />}>
                      <Db2 />
                    </Suspense>,
                    isLeaf: true,
                    hwType: HwTypeKey.DB2
                  },
                  {
                    key: RoutePath.SQLSERVER,
                    title: 'Microsoft SQL Server',
                    path: RoutePath.SQLSERVER,
                    component: <Suspense fallback={<XmSpinner />}>
                      <SqlServer />
                    </Suspense>,
                    isLeaf: true,
                    hwType: HwTypeKey.SQLSERVER
                  },
                  {
                    key: RoutePath.MONGODB,
                    title: 'MongoDB',
                    path: RoutePath.MONGODB,
                    component: <Suspense fallback={<XmSpinner />}>
                      <MongoDB />
                    </Suspense>,
                    isLeaf: true,
                    hwType: HwTypeKey.MONGODB
                  },
                  {
                    key: RoutePath.ORACLEDB,
                    title: 'Oracle',
                    path: RoutePath.ORACLEDB,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Oracle />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.ORACLEDB
                  },
                  {
                    key: RoutePath.POSTGRES,
                    title: 'PostgreSQL',
                    path: RoutePath.POSTGRES,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Postgres />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.POSTGRES
                  },
                ],
              },
              {
                key: RoutePath.CONTAINER,
                title: 'Container',
                path: RoutePath.CONTAINER,
                isLeaf: false,
                children: [
                  {
                    key: RoutePath.DOCKER_CFG,
                    title: 'Docker',
                    path: RoutePath.DOCKER_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Docker />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.DOCKER
                  },
                  {
                    key: RoutePath.KUBERNETES_CFG,
                    title: 'Kubernetes',
                    path: RoutePath.KUBERNETES_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Kubernetes />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.KUBERNETES
                  },
                  {
                    key: RoutePath.OPENSHIFT_CFG,
                    title: 'Red Hat OpenShift',
                    path: RoutePath.OPENSHIFT_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <OpenShift />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.OPENSHIFT
                  },

                  {
                    key: RoutePath.TANZU_CFG,
                    title: 'VMware Tanzu',
                    path: RoutePath.TANZU_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Tanzu />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.TANZU
                  },
                ],
              },
              {
                key: RoutePath.CLOUD,
                title: 'Cloud',
                path: RoutePath.CLOUD,
                isLeaf: false,
                children: [
                  {
                    key: RoutePath.AMAZON_CFG,
                    title: 'Amazon Web Services',
                    path: RoutePath.AMAZON_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Amazon />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.AWS
                  },
                  {
                    key: RoutePath.CLOUDSTACK,
                    title: 'Apache CloudStack',
                    path: RoutePath.CLOUDSTACK,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <MissingTech technoName="Apache CloudStack" />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.CLOUDSTACK
                  },
                  {
                    key: RoutePath.GCLOUD_CFG,
                    title: 'Google Cloud',
                    path: RoutePath.GCLOUD_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Gcloud />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.GCLOUD
                  },
                  {
                    key: RoutePath.IBMCLOUD,
                    title: 'IBM Cloud',
                    path: RoutePath.IBMCLOUD,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <IBMCloud />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.IBMCLOUD,
                  },
                  {
                    key: RoutePath.AZURE_CFG,
                    title: 'Microsoft Azure',
                    path: RoutePath.AZURE_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Azure />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.AZURE
                  },
                  {
                    key: RoutePath.SKYTAP_CFG,
                    title: 'Skytap',
                    path: RoutePath.SKYTAP_CFG,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <Skytap />
                      </Suspense>
                    ),
                    isLeaf: true,
                    hwType: HwTypeKey.SKYTAP,
                  },
                ],
              },
              {
                key: RoutePath.NETWORK,
                title: 'Network',
                path: RoutePath.NETWORK,
                isLeaf: false,
                children: [
                  {
                    key: RoutePath.SAN,
                    title: 'SAN',
                    path: RoutePath.SAN,
                    classType: ClassKey.SAN,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <SanHostCfg />
                      </Suspense>
                    ),
                    isLeaf: true,
                  },
                  {
                    key: RoutePath.LAN_CFG,
                    title: 'LAN',
                    path: RoutePath.LAN_CFG,
                    classType: ClassKey.LAN,
                    component: (
                      <Suspense fallback={<XmSpinner />}>
                        <LanHostCfg />
                      </Suspense>
                    ),
                    isLeaf: true,
                  },
                  // {
                  //   key: 'set-san-nav-cfg',
                  //   title: 'SANnav',
                  //   path: RoutePath.SAN_NAV_CFG,
                  //   component: (
                  //     <Suspense fallback={<Spinner />}>
                  //       <Azure />
                  //     </Suspense>
                  //   ),
                  //   isLeaf: true,
                  // },
                ],
              },
              {
                key: RoutePath.BACKUP,
                title: 'Backup',
                path: RoutePath.BACKUP,
                classType: ClassKey.BACKUP,
                isLeaf: true,
                component: <Suspense fallback={<XmSpinner />}>
                  <BackupHostCfg />
                </Suspense>,
              },
              {
                key: RoutePath.TAPE,
                title: 'Tape',
                path: RoutePath.TAPE,
                classType: ClassKey.TAPE,
                isLeaf: true,
                component: <Suspense fallback={<XmSpinner />}>
                  <TapeHostCfg />
                </Suspense>,
              },
            ],
          },
          MenuNode.getDivider('cfg-div0', false),
          {
            key: RoutePath.SET_CG,
            title: 'Custom Group',
            path: RoutePath.SET_CG,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <CustomGroups />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.SET_DASH_MANAGE,
            title: 'Dashboard',
            path: RoutePath.SET_DASH_MANAGE,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Dashboard />
              </Suspense>
            ),
            isLeaf: true,
            blockReadonly: true,
          },
          {
            key: RoutePath.GROUPING,
            title: 'Grouping',
            path: RoutePath.GROUPING,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Grouping />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.SET_HEATMAP_TIERS,
            title: 'Storage Tier',
            path: RoutePath.SET_HEATMAP_TIERS,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <HeatmapTierHome />
              </Suspense>
            ),
            adminFeature: 'heatmapTiers',
            isLeaf: true,
          },
          MenuNode.getDivider('cfg-div1', true),
          {
            key: RoutePath.SET_ALIAS,
            title: 'Alias',
            path: RoutePath.SET_ALIAS,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Aliases />
              </Suspense>
            ),
            isLeaf: true,
            treeOnly: true,
            adminOnly: true,
          },
          {
            key: RoutePath.SET_CONF_IMP_EXP,
            title: 'Device Import/Export',
            path: RoutePath.SET_CONF_IMP_EXP,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <ExpImpCfg />
              </Suspense>
            ),
            isLeaf: true,
            treeOnly: true,
            adminOnly: true
          },
          {
            key: RoutePath.SET_PROXY,
            title: 'Proxy',
            path: RoutePath.SET_PROXY,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <ProxyAgent />
              </Suspense>
            ),
            isLeaf: true,
            treeOnly: true,
            adminOnly: true
          },
          MenuNode.getDivider('cfg-div2', false),
          {
            key: RoutePath.SET_BACKEND,
            title: <XmWarningBadge offset={[3, 0]} hostcfg={false} license={false} certificate={true}>Application</XmWarningBadge>,
            titleText: 'Application',
            path: RoutePath.SET_BACKEND,
            adminOnly: true,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <ApplicationCfg />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.USERMANAGEMENT,
            title: 'Users',
            path: RoutePath.USERMANAGEMENT,
            adminOnly: true,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Management />
              </Suspense>
            ),
            isLeaf: true,
          },
        ],
      },
      {
        key: RoutePath.TOOLS,
        title: 'Tools',
        path: RoutePath.TOOLS,
        isLeaf: false,
        children: [
          {
            key: RoutePath.ADVISOR,
            title: 'Advisor',
            path: RoutePath.ADVISOR,
            children: [
              {
                key: RoutePath.ADVISOR + '-power',
                path: RoutePath.ADVISOR + '-power',
                title: 'IBM Power Systems',
                component: <BasePage url={API_URL.ADVISOR_POWER} />,
                isLeaf: true,
              },
              {
                key: RoutePath.ADVISOR + "-vmware",
                path: RoutePath.ADVISOR + "-vmware",
                title: "VMware",
                component: <BasePage url={API_URL.ADVISOR_VMWARE} />,
                isLeaf: true,
              },
              {
                key: RoutePath.ADVISOR + "-nutanix",
                path: RoutePath.ADVISOR + "-nutanix",
                title: "Nutanix",
                component: <BasePage url={API_URL.ADVISOR_NUTANIX} />,
                isLeaf: true,
              },
              {
                key: RoutePath.ADVISOR + "-proxmox",
                path: RoutePath.ADVISOR + "-proxmox",
                title: "Proxmox",
                component: <BasePage url={API_URL.ADVISOR_PROXMOX} />,
                isLeaf: true,
              },
              {
                key: RoutePath.ADVISOR + "-linux",
                path: RoutePath.ADVISOR + "-linux",
                title: "Linux",
                component: <BasePage url={API_URL.ADVISOR_LINUX} />,
                isLeaf: true,
              },
              {
                key: RoutePath.ADVISOR + "-kubernetes",
                path: RoutePath.ADVISOR + "-kubernetes",
                title: "Kubernetes",
                component: <BasePage url={API_URL.ADVISOR_KUBERNETES} />,
                isLeaf: true,
              },
              {
                key: RoutePath.ADVISOR + "-ovirt",
                path: RoutePath.ADVISOR + "-ovirt",
                title: "oVirt",
                component: <BasePage url={API_URL.ADVISOR_OVIRT} />,
                isLeaf: true,
              },
              {
                key: RoutePath.ADVISOR + "-windows",
                path: RoutePath.ADVISOR + "-windows",
                title: "Windows",
                component: <BasePage url={API_URL.ADVISOR_WINDOWS} />,
                isLeaf: true,
              }
            ]
          },
          {
            key: RoutePath.ALERTING,
            title: 'Alerting',
            path: RoutePath.ALERTING,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Alerting />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.EVENTS,
            title: 'Change Tracker',
            path: RoutePath.EVENTS,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <CFGTracker />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.CMDB,
            title: 'CMDB',
            path: RoutePath.CMDB,
            isLeaf: false,
            children: [
              {
                key: RoutePath.CUSTOM_LINKS,
                title: 'Custom links',
                isLeaf: true,
                path: RoutePath.CUSTOM_LINKS,
                component: (
                  <Suspense fallback={<XmSpinner />}>
                    <CustomLinks />
                  </Suspense>
                ),
              },
            ],
          },
          {
            key: RoutePath.EXPORTER,
            title: 'Exporter',
            path: RoutePath.EXPORTER,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Exporter />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.PUBLIC_API,
            title: <div
              onClick={(e) =>
                GLOB.app.modal.info({
                  title: 'Public API',
                  icon: null,
                  width: 'fit-content',
                  content: (
                    <div>
                      Internal documentation for our Public API is available at {' '}
                      <a href='/openapi.html' target='_blank'>
                        Internal Public API Documentation
                      </a>.
                      <br />
                      External documentation is available on our website at{' '}
                      <a href='https://xormon.com/public-api.php' target='_blank'>
                        External Public API Documentation
                      </a>.
                    </div>
                  ),
                })
              }
            >
              Public API
            </div>,
            isLeaf: true,
          },
          {
            key: RoutePath.REPORTER,
            title: 'Reporter',
            path: RoutePath.REPORTER,
            notInProduction: false,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Reporter />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.TAGS,
            title: 'Tags',
            path: RoutePath.TAGS,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Tag />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.TOPOLOGY,
            title: 'Topology',
            path: RoutePath.TOPOLOGY,
            isLeaf: false,
            children: topologyChildren,
          },
        ],
      },
      MenuNode.getDivider('cfg-div4', false),
      {
        key: RoutePath.HELP,
        title: <XmWarningBadge offset={[3, 0]} license={true} hostcfg={false} certificate={false}>Help</XmWarningBadge>,
        titleText: 'Help',
        path: RoutePath.HELP,
        children: [
          {
            key: RoutePath.DOCUMENTATION,
            title: <div
              onClick={(e) =>
                GLOB.app.modal.info({
                  title: 'Documentation',
                  icon: null,
                  width: 'fit-content',
                  content: (
                    <div>
                      External documentation is available on our website at{' '}
                      <a href='https://xormon.com/documentation-ng.php' target='_blank'>
                        https://xormon.com/documentation-ng.php
                      </a>.
                    </div>
                  ),
                })
              }
            >
              Documentation
            </div>,
            isLeaf: true,
          },
          {
            key: 'feedback',
            title: <div
              onClick={(e) =>
                GLOB.app.modal.info({
                  title: 'Feedback',
                  icon: null,
                  width: 'fit-content',
                  content: (
                    <div>
                      Use this link{' '}
                      <a href='https://xormon.com/contact-ng.php#feedback' target='_blank'>
                        xormon.com/contact-ng.php#feedback
                      </a>{' '}
                      if you want to pass us any feedback.
                      <br />
                      Thanks.
                    </div>
                  ),
                })
              }
            >
              Feedback
            </div>,
            isLeaf: true,
          },
          {
            key: RoutePath.LICENSE,
            title: <XmWarningBadge offset={[3, 0]} license={true} hostcfg={false} certificate={false}>License</XmWarningBadge>,
            titleText: 'License',
            path: RoutePath.LICENSE,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <License />
              </Suspense>
            ),
            adminOnly: true,
            isLeaf: true,
          },
          {
            key: RoutePath.SET_LOGS,
            title: 'Logs',
            path: RoutePath.SET_LOGS,
            managerOnly: true,
            isLeaf: false,
            children: [],
          },
          {
            key: RoutePath.SUPPORT,
            title: 'Support',
            titleText: 'Support',
            path: RoutePath.SUPPORT,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Support />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: RoutePath.UPGRADE,
            title: 'Upgrade',
            path: RoutePath.UPGRADE,
            adminOnly: true,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <UpgradeApp />
              </Suspense>
            ),
            isLeaf: true,
            notOnDocker: true
          },
          {
            key: RoutePath.ABOUT,
            title: 'About',
            path: RoutePath.ABOUT,
            component: (
              <Suspense fallback={<XmSpinner />}>
                <About />
              </Suspense>
            ),
            isLeaf: true,
          },
          MenuNode.getDivider('help-div', true),
          {
            key: RoutePath.SET_SELF_MONITORING,
            title: 'Self Monitoring',
            path: RoutePath.SET_SELF_MONITORING,
            component: <BasePage url={API_URL.SELF_MONITORING} />,
            isLeaf: true,
            adminOnly: true
          },
        ]
      },
      {
        key: 'user-div',
        divider: true,
        isLeaf: true,
        notInDemo: true,
        notInProduction: true,
      },
      {
        key: RoutePath.PLAYGROUND,
        title: 'Playground',
        path: RoutePath.PLAYGROUND,
        notInDemo: true,
        notInProduction: true,
        children: [
          {
            key: 'plotly-pie',
            title: 'Pie',
            path: '/pie',
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Pie />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: 'plotly-plot',
            title: 'Plot',
            path: '/plot',
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Plot />
              </Suspense>
            ),
            isLeaf: true,
          },
          {
            key: 'plotly-sunburst',
            title: 'Sunburst',
            path: '/sunburst',
            component: (
              <Suspense fallback={<XmSpinner />}>
                <Sunburst />
              </Suspense>
            ),
            isLeaf: true,
          },
        ],
      },
    ],
  },
  {
    key: 'user',
    icon: <UserOutlined />,
    children: [
      {
        key: 'userName',
        title: '',
        disabled: true,
        isLeaf: true,
      },
      {
        key: 'user-div',
        divider: true,
        isLeaf: true,
      },
      {
        key: RoutePath.PROFILE,
        title: 'Profile',
        path: RoutePath.PROFILE,
        component: <Suspense fallback={<XmSpinner />}><ProfileTabs /></Suspense>,
        isLeaf: true,
      },
      {
        key: RoutePath.LOGOUT,
        title: 'Logout',
        path: RoutePath.LOGOUT,
        isLeaf: true,
      },
    ],
  },
];
